import React, { useState } from 'react';
import { Check, GripVertical, Plus, RotateCcw, SlidersHorizontal } from 'lucide-react';
import type { LayoutSaveState } from '../../hooks/useDashboardLayout';
import { DEFAULT_PRESET, DashboardLayout, KpiId, PRESET_BY_ID, PresetId, WIDGET_BY_ID, WidgetId, hiddenWidgets, matchingPreset } from './dashboardLayout';
import { setDragPayload } from './dashboardEditing';
import { KpiChooser, PresetPicker, SaveStatus } from './PresetPicker';

/**
 * Die Leiste ueber dem Dashboard im Anpassen-Modus (PC): Vorlagen,
 * Zuruecksetzen mit Rueckfrage an Ort und Stelle, Kachelauswahl, Ablage der
 * ausgeblendeten Bausteine, Speicherstatus und „Fertig".
 */

const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{children}</h3>
);

/** „Zurücksetzen auf Vorlage" mit Rueckfrage in derselben Zeile — kein window.confirm. */
const ResetControl: React.FC<{ disabled: boolean; presetTitle: string; onReset: () => void }> = ({ disabled, presetTitle, onReset }) => {
    const [confirm, setConfirm] = useState(false);
    if (confirm) {
        return (
            <span role="group" aria-label="Zurücksetzen bestätigen" className="inline-flex items-center gap-1.5 flex-wrap text-[11px] text-slate-700">
                <span>Eigenen Aufbau verwerfen und „{presetTitle}“ laden?</span>
                <button type="button" onClick={() => { setConfirm(false); onReset(); }} className="h-7 px-2 rounded-md bg-slate-900 text-white font-semibold hover:bg-slate-800">
                    Ja, zurücksetzen
                </button>
                <button type="button" autoFocus onClick={() => setConfirm(false)} className="h-7 px-2 rounded-md border border-slate-200 font-semibold text-slate-600 hover:border-slate-300 hover:text-slate-900">
                    Abbrechen
                </button>
            </span>
        );
    }
    return (
        <button
            type="button"
            disabled={disabled}
            onClick={() => setConfirm(true)}
            title={disabled ? 'Entspricht bereits einer Vorlage' : undefined}
            className="inline-flex items-center gap-1 h-7 px-2 rounded-md text-[11px] font-semibold text-slate-600 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-slate-600"
        >
            <RotateCcw className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
            Zurücksetzen auf Vorlage
        </button>
    );
};

interface TrayProps {
    layout: DashboardLayout;
    onShow: (id: WidgetId) => void;
    onDragStart: (id: WidgetId) => void;
    onDragEnd: () => void;
}

/** Ausgeblendete Bausteine: per Knopf an die Stammzone oder per Ziehen an eine Stelle. */
const WidgetTray: React.FC<TrayProps> = ({ layout, onShow, onDragStart, onDragEnd }) => {
    const hidden = hiddenWidgets(layout);
    if (hidden.length === 0) {
        return (
            <p className="text-[11px] text-slate-500 rounded-lg border border-dashed border-slate-300 px-3 py-2.5">
                Alle Bausteine sind eingeblendet. Was du am Baustein ausblendest, wartet hier.
            </p>
        );
    }
    return (
        <ul className="space-y-1.5" aria-label="Ausgeblendete Bausteine">
            {hidden.map(id => {
                const w = WIDGET_BY_ID[id];
                return (
                    <li
                        key={id}
                        draggable
                        onDragStart={e => { setDragPayload(e.dataTransfer, id); window.setTimeout(() => onDragStart(id), 0); }}
                        onDragEnd={onDragEnd}
                        title={w.description}
                        className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white pl-1.5 pr-1.5 py-1.5 cursor-grab active:cursor-grabbing"
                    >
                        <GripVertical className="w-4 h-4 text-slate-400 shrink-0" aria-hidden="true" />
                        <span className="min-w-0 flex-1">
                            <span className="block text-[12px] font-semibold text-slate-800 truncate">{w.title}</span>
                            <span className="block text-[11px] text-slate-500 truncate">{w.description}</span>
                        </span>
                        <button
                            type="button"
                            onClick={() => onShow(id)}
                            aria-label={`„${w.title}“ hinzufügen`}
                            className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-slate-200 text-[11px] font-semibold text-slate-700 hover:border-slate-300 hover:bg-slate-50 shrink-0"
                        >
                            <Plus className="w-3.5 h-3.5" aria-hidden="true" />
                            Hinzufügen
                        </button>
                    </li>
                );
            })}
        </ul>
    );
};

interface Props {
    layout: DashboardLayout;
    saveState: LayoutSaveState;
    onRetrySave: () => void;
    onChoosePreset: (id: PresetId) => void;
    onReset: () => void;
    onToggleKpi: (id: KpiId) => void;
    onShow: (id: WidgetId) => void;
    onDragStart: (id: WidgetId) => void;
    onDragEnd: () => void;
    onDone: () => void;
}

export const DashboardCustomizer: React.FC<Props> = ({ layout, saveState, onRetrySave, onChoosePreset, onReset, onToggleKpi, onShow, onDragStart, onDragEnd, onDone }) => {
    const active = matchingPreset(layout);
    return (
        <section aria-label="Dashboard anpassen" className="rounded-xl border border-blue-200 bg-white animate-slide-down">
            <div className="flex items-start justify-between gap-4 flex-wrap px-4 py-3 border-b border-slate-100">
                <div className="min-w-0 flex-1">
                    <h2 className="text-[13px] font-semibold text-slate-900 flex items-center gap-2">
                        <SlidersHorizontal className="w-4 h-4 text-blue-700 shrink-0" aria-hidden="true" />
                        Dashboard anpassen
                    </h2>
                    <p className="text-[11px] text-slate-500 mt-0.5 max-w-[80ch]">
                        Bausteine am Griff ziehen oder mit den Pfeilen verschieben, ausblenden und wieder hinzufügen.
                        Dein Aufbau wird auf dem Server gespeichert und gilt auf jedem Gerät. Esc beendet.
                    </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                    <SaveStatus state={saveState} onRetry={onRetrySave} />
                    <button type="button" onClick={onDone} className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg bg-slate-900 text-white text-[12px] font-semibold hover:bg-slate-800">
                        <Check className="w-4 h-4" aria-hidden="true" />
                        Fertig
                    </button>
                </div>
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)] gap-x-6 gap-y-4 px-4 py-3">
                <div className="min-w-0 space-y-2">
                    <div className="flex items-center justify-between gap-3 flex-wrap min-h-7">
                        <Label>Vorlagen</Label>
                        <ResetControl disabled={active !== null} presetTitle={PRESET_BY_ID[DEFAULT_PRESET].title} onReset={onReset} />
                    </div>
                    <PresetPicker layout={layout} onChoose={onChoosePreset} />
                    <p className="text-[11px] text-slate-500">
                        {active ? `Vorlage „${PRESET_BY_ID[active].title}“ – unverändert.` : 'Eigene Anordnung – keine Vorlage entspricht genau deinem Aufbau.'}
                    </p>
                </div>
                <div className="min-w-0 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2 gap-x-6 gap-y-4">
                    <div className="min-w-0 space-y-2">
                        <Label>Kacheln „Heute im Blick“</Label>
                        <KpiChooser layout={layout} onToggle={onToggleKpi} />
                    </div>
                    <div className="min-w-0 space-y-2">
                        <Label>Ausgeblendete Bausteine</Label>
                        <WidgetTray layout={layout} onShow={onShow} onDragStart={onDragStart} onDragEnd={onDragEnd} />
                    </div>
                </div>
            </div>
        </section>
    );
};
